import type { CommandPendingProcessingVersion } from '@festina/legalentity-general-types/legalentity'
import type { CommitmentCommandPendingProcessingVersion } from '@festina/life-general-types/life'
import { type Except, notEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

const isLifeCommitmentCommandPendingProcessingVersion = (
  x: Optional<CommandPendingProcessingVersion>,
): x is CommitmentCommandPendingProcessingVersion =>
  x?._type === 'CommitmentCommandPendingProcessingVersion'

export const useUnappliedCommandReasons = (
  reasonForBeingUnapplied: Optional<CommandPendingProcessingVersion>,
) => {
  const translate = useTranslate()

  return useMemo(() => {
    const reasons: Record<
      keyof Except<
        NonNullable<typeof reasonForBeingUnapplied>,
        '_type' | 'cancelPendingTimeStamp' | 'cancelPendingUserId'
      >,
      string | undefined
    > = {
      approvalPending: reasonForBeingUnapplied?.approvalPending
        ? translate('commandData.reasonForBeingUnapplied.approvalPending')
        : undefined,
      cancelApprovalPending: reasonForBeingUnapplied?.cancelApprovalPending
        ? translate('commandData.reasonForBeingUnapplied.cancelApprovalPending')
        : undefined,
      futureApplyDate: reasonForBeingUnapplied?.futureApplyDate
        ? translate('commandData.reasonForBeingUnapplied.futureApplyDate')
        : undefined,
    }

    if (
      isLifeCommitmentCommandPendingProcessingVersion(
        reasonForBeingUnapplied,
      ) &&
      reasonForBeingUnapplied?.missingPersons
    ) {
      const allCommitmentReasons: Record<
        keyof Except<
          NonNullable<CommitmentCommandPendingProcessingVersion>,
          '_type' | 'cancelPendingTimeStamp' | 'cancelPendingUserId'
        >,
        string | undefined
      > = {
        ...reasons,
        missingPersons: reasonForBeingUnapplied?.missingPersons
          ? translate('commandData.reasonForBeingUnapplied.missingPersons')
          : undefined,
      }

      return Object.values(allCommitmentReasons).filter(notEmpty).join(', ')
    }

    return Object.values(reasons).filter(notEmpty).join(', ')
  }, [reasonForBeingUnapplied, translate])
}
